import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Checkbox, FormControlLabel,
  Button, TextField, Stack, Autocomplete, FormControl, InputLabel, Select, MenuItem, Box, Collapse, Typography,
  List, ListItem, ListItemText, IconButton, Chip
} from '@mui/material';
import supabase from '../../../../utils/supabase';
import PublicationSelector from './parts/PublicationSelector';
import ResearcherSelector from './parts/ResearcherSelector';
import RepositorySelector from './parts/RepositorySelector';

const recordTypes = [
  { id: 'literature_only', label: '文献内の記述のみ' },
  { id: 'specimen_based_literature', label: '標本に基づいた文献記録' },
  { id: 'specimen_record', label: '標本記録' },
];

const validityOptions = [
  { id: 'valid', label: 'Valid' },
  { id: 'invalid', label: 'Invalid' },
  { id: 'doubtful', label: 'Doubtful' },
  { id: 'unassessed', label: 'Unassessed' },
];

const sexOptions = ['Male', 'Female', 'Intersex', 'Unknown'];

export default function DialogOccurrenceEditor({
  open,
  onClose,
  onSave,
  occurrence,
  scientificNameId,
  publications = [],
  countries = [],
}) {
  const [form, setForm] = useState({
    record_type: 'literature_only',
    country_id: null,
    locality: '',
    latitude: '',
    longitude: '',
    altitude: '',
    is_type_locality: false,
    is_specimen_based: false, // will be derived from record_type
    repository_id: null,
    sampling_method: '',
    sex: '',
    individuals: '',
    observed_date: '',
    validity: 'valid',
    publication_id: null,
    page: '',
    remarks: '',
    environmental_note: '',
    observation_note: '',
  });

  // State for repository object, as RepositorySelector works with objects
  const [repository, setRepository] = useState(null);

  // State for multiple collectors
  const [collectors, setCollectors] = useState([]);

  const isSpecimenBased = form.record_type === 'specimen_based_literature' || form.record_type === 'specimen_record';

  useEffect(() => {
    if (open) {
      setRepository(null); // Reset on open
      setCollectors([]); // Reset collectors
      if (occurrence) {
        setForm({
          record_type: occurrence.record_type || 'literature_only',
          country_id: occurrence.country_id || null,
          locality: occurrence.locality || '',
          latitude: occurrence.latitude || '',
          longitude: occurrence.longitude || '',
          altitude: occurrence.altitude || '',
          is_type_locality: occurrence.is_type_locality || false,
          is_specimen_based: occurrence.is_specimen_based || false,
          repository_id: occurrence.repository_id || null,
          sampling_method: occurrence.sampling_method || '',
          sex: occurrence.sex || '',
          individuals: occurrence.individuals || '',
          observed_date: occurrence.observed_date || '',
          validity: occurrence.validity || 'valid',
          publication_id: occurrence.publication_id || null,
          page: occurrence.page || '',
          remarks: occurrence.remarks || '',
          environmental_note: occurrence.environmental_note || '',
          observation_note: occurrence.observation_note || '',
        });

        // Fetch full repository object for the selector
        if (occurrence.repository_id) {
          supabase.from('Repositories')
            .select('*')
            .eq('uuid', occurrence.repository_id)
            .single()
            .then(({ data }) => data && setRepository(data));
        }

        // Fetch collectors
        supabase.from('occurrence_collectors')
          .select('researcher_id, collector_order, researchers(*)')
          .eq('occurrence_id', occurrence.id)
          .order('collector_order')
          .then(({ data, error }) => {
            if (error) console.error("Error fetching collectors:", error);
            if (data) setCollectors(data.map(d => d.researchers));
          });
      } else {
        setForm({
          record_type: 'literature_only',
          country_id: null,
          locality: '',
          latitude: '',
          longitude: '',
          altitude: '',
          is_type_locality: false,
          is_specimen_based: false,
          repository_id: null,
          sampling_method: '',
          sex: '',
          individuals: '',
          observed_date: '',
          validity: 'valid',
          publication_id: null,
          page: '',
          remarks: '',
          environmental_note: '',
          observation_note: '',
        });
      }
    }
  }, [open, occurrence]);

  // record_type に応じて is_specimen_based を自動更新
  useEffect(() => {
    setForm(f => ({...f, is_specimen_based: isSpecimenBased}));
  }, [isSpecimenBased]);

  const handleSave = async () => {
    if (!form.country_id) {
      alert("Please select a country.");
      return;
    }

    let pubUuid = null;
    if (form.publication_id) {
        const selectedPub = publications.find(p => p.id === form.publication_id);
        pubUuid = selectedPub?.id_uuid;
        if (!pubUuid) {
            const { data } = await supabase.from('publications').select('id_uuid').eq('id', form.publication_id).maybeSingle();
            pubUuid = data?.id_uuid;
        }
    }

    const payload = {
      scientific_name_id: scientificNameId,
      record_type: form.record_type,
      country_id: form.country_id,
      locality: form.locality || null,
      latitude: form.latitude ? parseFloat(form.latitude) : null,
      longitude: form.longitude ? parseFloat(form.longitude) : null,
      altitude: form.altitude ? parseFloat(form.altitude) : null,
      is_type_locality: form.is_type_locality,
      is_specimen_based: isSpecimenBased,
      repository_id: isSpecimenBased ? repository?.uuid : null, // RepositorySelector uses object
      sampling_method: isSpecimenBased ? form.sampling_method : null,
      sex: isSpecimenBased ? form.sex : null,
      individuals: isSpecimenBased && form.individuals ? parseInt(form.individuals, 10) : null,
      observed_date: form.observed_date || null,
      validity: form.validity,
      publication_id: form.publication_id || null,
      publication_id_uuid: pubUuid || null,
      page: form.page || null,
      remarks: form.remarks || null,
      environmental_note: form.environmental_note || null,
      observation_note: form.observation_note || null,
    };

    let error;
    if (occurrence?.id) {
      const { error: e } = await supabase.from('occurrences').update(payload).eq('id', occurrence.id).select();
      error = e;
    } else {
      const { data, error: e } = await supabase.from('occurrences').insert([payload]).select().single();
      error = e;
      // 新規作成時はIDを取得して collectors の保存に使う
      if (data) occurrence = data;
    }

    // Save collectors
    if (!error && occurrence?.id) {
      await supabase.from('occurrence_collectors').delete().eq('occurrence_id', occurrence.id);
      if (collectors.length > 0) {
        const collectorRows = collectors.map((c, i) => ({
          occurrence_id: occurrence.id,
          researcher_id: c.id,
          collector_order: i + 1
        }));
        const { error: cErr } = await supabase.from('occurrence_collectors').insert(collectorRows);
        if (cErr) console.error("Error saving collectors:", cErr);
      }
    }

    if (error) {
      alert('Error saving occurrence: ' + error.message);
    } else {
      onSave?.();
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{occurrence ? 'Edit Occurrence' : 'Add Occurrence'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Record Type</InputLabel>
            <Select
              value={form.record_type}
              label="Record Type"
              onChange={(e) => setForm({ ...form, record_type: e.target.value })}
            >
              {recordTypes.map((rt) => (
                <MenuItem key={rt.id} value={rt.id}>{rt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel>Validity</InputLabel>
            <Select
              value={form.validity}
              label="Validity"
              onChange={(e) => setForm({ ...form, validity: e.target.value })}
            >
              {validityOptions.map((vo) => (
                <MenuItem key={vo.id} value={vo.id}>{vo.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Autocomplete
            options={countries}
            getOptionLabel={(option) => option.name_english || option.id}
            value={countries.find(c => c.id === form.country_id) || null}
            onChange={(_, val) => setForm({ ...form, country_id: val?.id || null })}
            renderInput={(params) => <TextField {...params} label="Country" size="small" required />}
          />
          <TextField label="Locality" value={form.locality} onChange={(e) => setForm({ ...form, locality: e.target.value })} size="small" />
          <Stack direction="row" spacing={2}>
            <TextField label="Latitude" type="number" value={form.latitude} onChange={(e) => setForm({ ...form, latitude: e.target.value })} size="small" fullWidth />
            <TextField label="Longitude" type="number" value={form.longitude} onChange={(e) => setForm({ ...form, longitude: e.target.value })} size="small" fullWidth />
            <TextField label="Altitude (m)" type="number" value={form.altitude} onChange={(e) => setForm({ ...form, altitude: e.target.value })} size="small" fullWidth />
          </Stack>
          <TextField label="Observed Date" type="date" value={form.observed_date} onChange={(e) => setForm({ ...form, observed_date: e.target.value })} size="small" InputLabelProps={{ shrink: true }} />
          <FormControlLabel
            control={
              <Checkbox checked={form.is_type_locality} onChange={(e) => setForm({ ...form, is_type_locality: e.target.checked })} />
            }
            label="Is this a type locality?"
          />
          
          <Collapse in={isSpecimenBased}>
            <Stack spacing={2} sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <Typography variant="subtitle2">Specimen Information</Typography>
              <FormControlLabel
                control={<Checkbox checked={form.is_specimen_based} readOnly />}
                label="Is specimen based"
              />
              
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }}>
                <Typography variant="caption" color="text.secondary">Collected By (Ordered)</Typography>
                <List dense>
                  {collectors.map((c, idx) => (
                    <ListItem key={c.id || idx} secondaryAction={
                      <Button size="small" color="error" onClick={() => setCollectors(prev => prev.filter((_, i) => i !== idx))}>Remove</Button>
                    }>
                      <ListItemText primary={`${idx + 1}. ${c.last_name}, ${c.first_name}`} />
                    </ListItem>
                  ))}
                </List>
                <ResearcherSelector 
                  label="Add Collector" 
                  value={null} 
                  onChange={(researcher) => {
                    if (researcher && !collectors.some(c => c.id === researcher.id)) {
                      setCollectors(prev => [...prev, researcher]);
                    }
                  }}
                  returnObject
                />
              </Box>

              <RepositorySelector label="Repository" value={repository} onChange={setRepository} />
              <TextField label="Sampling Method" value={form.sampling_method} onChange={(e) => setForm({ ...form, sampling_method: e.target.value })} size="small" />
              <Stack direction="row" spacing={2}>
                <Autocomplete options={sexOptions} freeSolo value={form.sex} onChange={(e, val) => setForm({ ...form, sex: val })} renderInput={(params) => <TextField {...params} label="Sex" size="small" />} fullWidth />
                <TextField label="Individuals" type="number" value={form.individuals} onChange={(e) => setForm({ ...form, individuals: e.target.value })} size="small" fullWidth />
              </Stack>
            </Stack>
          </Collapse>

          <TextField label="Environmental Note" value={form.environmental_note} onChange={(e) => setForm({ ...form, environmental_note: e.target.value })} multiline rows={2} size="small" />
          <TextField label="Observation Note" value={form.observation_note} onChange={(e) => setForm({ ...form, observation_note: e.target.value })} multiline rows={2} size="small" />

          <PublicationSelector label="Publication (Source)" value={form.publication_id} onChange={(id) => setForm({ ...form, publication_id: id })} publicationsData={publications} />
          <TextField label="Page" value={form.page} onChange={(e) => setForm({ ...form, page: e.target.value })} size="small" />
          <TextField label="Remarks" value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} multiline rows={2} size="small" />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}
